import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const TIMES = ["9:00","9:30","10:00","10:30","11:00","11:30","1:00","1:30","2:00","2:30","3:00","3:30","4:00"];

export default function BookConsult() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [step, setStep] = useState(1);
  const [providerId, setProviderId] = useState(params.get("provider") || "");
  const [ctype, setCtype] = useState("");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", goals: "", notes: "" });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/providers").then(({data})=>setProviders(data)); }, []);
  useEffect(() => { if (user) setForm(f=>({...f, name: user.name, email: user.email, phone: user.phone||""})); }, [user]);

  const provider = useMemo(()=> providers.find(p=>p.id===providerId), [providers, providerId]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/consultations/request", {
        provider: providerId, consultation_type: ctype,
        date: format(date, "yyyy-MM-dd"), time, ...form,
      });
      setDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Please try again.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="section-y" data-testid="consult-success">
        <div className="container-x max-w-xl text-center">
          <CheckCircle2 className="w-16 h-16 text-sage-600 mx-auto" strokeWidth={1.2} />
          <h1 className="font-display text-4xl text-sage-800 mt-6">Your appointment request has been received.</h1>
          <p className="mt-4 text-sage-800/70">You'll receive confirmation once it has been approved by the Restore Wellness team.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="section-y" data-testid="book-consult-page">
      <div className="container-x max-w-4xl">
        <div className="overline">Book a Consultation</div>
        <h1 className="font-display text-4xl lg:text-5xl text-sage-800 mt-3">Let's design a plan that fits you.</h1>

        {/* Step 1: provider */}
        <section className="mt-12">
          <div className="text-sm text-sage-800/60 mb-3">Step 1 — Choose your provider</div>
          <div className="grid sm:grid-cols-2 gap-5">
            {providers.map((p) => (
              <button key={p.id} data-testid={`select-provider-${p.id}`} onClick={()=>{setProviderId(p.id); setStep(2);}} className={`card-lift text-left rounded-3xl bg-white border p-5 flex gap-4 items-center ${providerId===p.id?"border-sage-700 ring-2 ring-sage-700/10":"border-sand-200/70"}`}>
                <img src={p.photo} alt={p.name} className="w-20 h-20 object-cover rounded-2xl" />
                <div>
                  <div className="font-display text-xl text-sage-800">{p.name}</div>
                  <div className="text-xs text-clay-600 uppercase tracking-widest mt-0.5">{p.role}</div>
                  <div className="text-sm text-sage-800/70 mt-2 line-clamp-2">{p.intro}</div>
                </div>
              </button>
            ))}
          </div>
        </section>

        {/* Step 2: consult type */}
        {providerId && (
          <section className="mt-12" data-testid="consult-type-section">
            <div className="text-sm text-sage-800/60 mb-3">Step 2 — Consultation type</div>
            <Select value={ctype} onValueChange={(v)=>{setCtype(v); setStep(3);}}>
              <SelectTrigger className="max-w-md rounded-xl h-12" data-testid="ctype-select"><SelectValue placeholder="Select consultation type" /></SelectTrigger>
              <SelectContent>
                {provider?.consultation_types?.map((t)=>(<SelectItem key={t} value={t}>{t}</SelectItem>))}
              </SelectContent>
            </Select>
          </section>
        )}

        {/* Step 3: date + time */}
        {ctype && (
          <section className="mt-12 grid lg:grid-cols-2 gap-8" data-testid="date-time-section">
            <div>
              <div className="text-sm text-sage-800/60 mb-3">Step 3 — Pick a date</div>
              <div className="rounded-3xl bg-white border border-sand-200/70 p-3 w-fit">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: new Date() }} data-testid="calendar" />
              </div>
            </div>
            <div>
              <div className="text-sm text-sage-800/60 mb-3">Available times</div>
              <div className="grid grid-cols-3 gap-2">
                {TIMES.map((t) => (
                  <button key={t} data-testid={`time-${t}`} onClick={()=>setTime(t)} disabled={!date} className={`rounded-full h-10 text-sm border transition-colors ${time===t?"bg-sage-700 text-white border-sage-700":"bg-white border-sand-300 text-sage-800 hover:bg-sand-100 disabled:opacity-40"}`}>{t}</button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Step 4: info */}
        {date && time && (
          <section className="mt-12 max-w-2xl" data-testid="contact-section">
            <div className="text-sm text-sage-800/60 mb-3">Step 4 — Your details</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Name</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="c-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="c-email" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="c-phone" /></div>
              <div className="sm:col-span-2"><Label>What are your main wellness goals?</Label><Textarea rows={3} value={form.goals} onChange={(e)=>setForm({...form,goals:e.target.value})} data-testid="c-goals" /></div>
            </div>
            <Button disabled={submitting || !form.name || !form.email} onClick={submit} className="btn-pill h-12 mt-6 bg-sage-700 hover:bg-sage-800 text-white px-8" data-testid="c-submit">
              {submitting ? "Requesting…" : "Request Appointment"}
            </Button>
            <p className="text-xs text-sage-800/50 mt-3">Bookings are reviewed and confirmed by the Restore Wellness team.</p>
          </section>
        )}
      </div>
    </div>
  );
}
